import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { notification } from "../../../utils/Notifications";
import useLogout from "../../../hooks/auth/authHook.js";
import {
  Button,
  Container,
  Dialog,
  Typography,
  TextField,
  CircularProgress,
  Switch,
  FormControlLabel,
  Grid,
} from "@mui/material";
import { updateCategory } from "../../../api/categoriesApi.js";
import styles from "./editCategoryStyles.js";

EditCategoryDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default function EditCategoryDialog({
  onClose,
  open,
  fetchCategories,
  category,
}) {
  const [logout] = useLogout();

  const [name, setName] = useState("");
  const [liberDiscount, setLiberDiscount] = useState("");
  const [thirdDiscount, setThirdDiscount] = useState("");
  const [productBuyDiscount, setProductBuyDiscount] = useState("");
  const [storageDiscount, setStorageDiscount] = useState("");
  const [supplierNotification, setSupplierNotification] = useState(false);
  const [clientsNotification, setClientsNotification] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateField = (value, fieldName) => {
    let error = "";
    if (!/^-?\d+$/.test(value)) {
    /* if (!/^\d+$/.test(value)) { */
      error = "El valor debe ser un número entero positivo";
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: error,
    }));
    return !error;
  };

  const handleNameChange = (event) => setName(event.target.value);
  const handleLiberDiscountChange = (event) => {
    setLiberDiscount(event.target.value);
    validateField(event.target.value, "liberDiscount");
  };

  const handleThirdDiscountChange = (event) => {
    setThirdDiscount(event.target.value);
    validateField(event.target.value, "thirdDiscount");
  };

  const handleProductBuyDiscountChange = (event) => {
    setProductBuyDiscount(event.target.value);
    validateField(event.target.value, "productBuyDiscount");
  };

  const handleStorageDiscountChange = (event) => {
    setStorageDiscount(event.target.value);
    validateField(event.target.value, "storageDiscount");
  };
  const handleSupplierNotification = (event) =>
    setSupplierNotification(event.target.checked);

  const handleClientsNotification = (event) =>
    setClientsNotification(event.target.checked);

  const clearFields = () => {
    setName("");
    setLiberDiscount("");
    setThirdDiscount("");
    setProductBuyDiscount("");
    setStorageDiscount("");
    setSupplierNotification(false);
    setClientsNotification(false);
    setErrors({});
  };

  const handleCreateAdminClick = () => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);

    updateCategory(
      category.id_membership_type,
      name,
      liberDiscount,
      thirdDiscount,
      productBuyDiscount,
      storageDiscount,
      supplierNotification,
      clientsNotification
    )
      .then((response) => {
        if (response.data.status_Code === 200 && response.data.status_Message === "membership name isn't available") {
          notification({ text: "El nombre ya esta en uso", type: "error" });
          setIsLoading(false);
          return;
        }
        if (response.data.status_Code === 200) {
          notification({ text: "Categoría actualizada", type: "success" });
          fetchCategories();
          setIsLoading(false);
          onClose();
        } else {
          notification({
            text: "Error al actualizar la categoría",
            type: "error",
          });
          setIsLoading(false);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        
        // Manejar diferentes tipos de errores
        if (error.response?.data?.status_Code === 401 || error.response?.data?.status_Code === 403) {
          // No hacer nada aquí, el interceptor de axios se encargará de la redirección
          return;
        }
        
        // Para otros errores, limpiar campos y mostrar notificación
        clearFields();
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      });
  };

  useEffect(() => {
    const validateForm = () => {
      const isValid =
        name !== undefined&&
        liberDiscount !== undefined&&
        thirdDiscount !== undefined&&
        productBuyDiscount !== undefined&&
        storageDiscount !== undefined;
      const hasNoErrors =
        !errors.name &&
        !errors.liberDiscount &&
        !errors.thirdDiscount &&
        !errors.productBuyDiscount &&
        !errors.storageDiscount;
      setIsFormValid(isValid && hasNoErrors);
    };    

    validateForm();
  }, [
    name,
    liberDiscount,
    thirdDiscount,
    productBuyDiscount,
    storageDiscount,
    errors,
  ]);

  /* useEffect(() => {
    if (!open) {
      clearFields();
    }
  }, []); */

  useEffect(() => {
    setName(category?.membership_name);
    setLiberDiscount(category?.liber_discount);
    setThirdDiscount(category?.third_discount);
    setProductBuyDiscount(category?.product_buy_discount);
    setStorageDiscount(category?.storage_discount);
    setSupplierNotification(category?.supplier_notification);
    setClientsNotification(category?.client_notification);
  }, [category]);

  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      onClose={handleDialogClose}
      open={open}
      PaperProps={{ style: styles.dialog }}
    >
      <Container sx={styles.mainContainer}>
        <Typography component="div" variant="h5" style={styles.title}>
          Editar categoría
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={12}>
            <TextField
              id="standard-basic"
              label="* Nombre"
              variant="standard"
              value={name}
              onChange={handleNameChange}
              error={!!errors.name}
              helperText={errors.name}
              inputProps={{ maxLength: 50 }}
              style={styles.textfield}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              id="standard-basic"
              label="* Descuento Liber"
              variant="standard"
              type="number"
              value={liberDiscount}
              onChange={handleLiberDiscountChange}
              error={!!errors.liberDiscount}
              helperText={errors.liberDiscount}
              style={styles.textfield}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              id="standard-basic"
              label="* Descuento de terceros"
              variant="standard"
              type="number"
              value={thirdDiscount}
              onChange={handleThirdDiscountChange}
              error={!!errors.thirdDiscount}
              helperText={errors.thirdDiscount}
              style={styles.textfield}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              id="standard-basic"
              label="* Descuento en productos"
              variant="standard"
              type="number"
              value={productBuyDiscount}
              onChange={handleProductBuyDiscountChange}
              error={!!errors.productBuyDiscount}
              helperText={errors.productBuyDiscount}
              style={styles.textfield}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              id="standard-basic"
              label="* Descuento en almacenamiento"
              variant="standard"
              type="number"
              value={storageDiscount}
              onChange={handleStorageDiscountChange}
              error={!!errors.storageDiscount}
              helperText={errors.storageDiscount}
              style={styles.textfield}
              fullWidth
            />
          </Grid>
        </Grid>

        <FormControlLabel
          control={
            <Switch
              checked={supplierNotification}
              onChange={handleSupplierNotification}
            />
          }
          label="Notificaciones de proveedores"
          sx={styles.check}
        />
        <FormControlLabel
          sx={styles.checkClients}
          control={
            <Switch
              checked={clientsNotification}
              onChange={handleClientsNotification}
            />
          }
          label="Notificaciones de clientes"
        />

        <Container style={styles.buttonsContainer}>
          <Button
            variant="standard"
            color="primary"
            onClick={handleDialogClose}
            style={styles.cancelButton}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!isFormValid}
            onClick={handleCreateAdminClick}
            sx={isFormValid ? styles.activeButton : styles.inactiveButton}
          >
            {isLoading ? (
              <CircularProgress size={24} style={styles.circularProgress} />
            ) : (
              "Guardar"
            )}
          </Button>
        </Container>
      </Container>
    </Dialog>
  );
}
